import type { ResumeContent } from './types';

/**
 * Click-to-edit on the rendered preview.
 *
 * Templates stamp `data-rf="<section>.<id>.<field>"` onto the element that
 * produced each value, which is the only link between a paragraph on screen and
 * the field behind it — the Handlebars render otherwise flattens the content
 * into a string and loses it. Ids rather than indices, so the mapping survives
 * reordering and lines up with the store's `updateEmployment(id, …)` API.
 *
 * This module is the pure half: parsing a path, reading the current value, and
 * turning edited markup back into field text. The DOM plumbing lives with the
 * preview component.
 */

export type PersonalInfoField = 'firstName' | 'lastName';
export type ContactField = 'email' | 'phone' | 'location';
export type EducationField = 'degree' | 'field' | 'institution' | 'gpa';
export type CertificationField = 'name' | 'issuer' | 'credentialUrl';
export type SkillListField = 'technical' | 'soft';

/**
 * A date is never edited as free text.
 *
 * `Jan 2025 - Present` is rendered from `startDate`/`endDate`/
 * `isCurrentlyWorking`; parsing a date range back out of whatever someone types
 * is a reliable way to corrupt the record. Dates carry their own kind so the
 * preview can open a picker instead of a caret.
 */
export type DateOwner = 'employment' | 'education' | 'certification';

export type FieldEdit =
  | { kind: 'profile'; field: 'headline' | 'summary'; value: string }
  | { kind: 'personalInfo'; field: PersonalInfoField; value: string }
  | { kind: 'contact'; field: ContactField; value: string }
  | { kind: 'employment'; id: string; field: 'title' | 'company' | 'responsibilities'; value: string }
  | { kind: 'education'; id: string; field: EducationField; value: string }
  | { kind: 'certification'; id: string; field: CertificationField; value: string }
  | { kind: 'skillList'; list: SkillListField; value: string }
  | { kind: 'skillCategory'; id: string; value: string }
  /**
   * One chip. Skills render as individual chips, so editing a chip in place is
   * both the natural gesture and safer than editing the container: a container
   * holding two loops (technical + soft, as two templates do) cannot be written
   * back without merging the lists.
   *
   * Clearing a chip removes that skill — the only sensible reading of deleting
   * all its text.
   */
  | { kind: 'skillItem'; list: SkillListField; index: number; value: string }
  | { kind: 'skillCategoryItem'; id: string; index: number; value: string }
  | { kind: 'customSection'; id: string; field: 'title' | 'content'; value: string }
  | { kind: 'date'; owner: DateOwner; id: string; field: string; value: string };

const PROFILE_FIELDS = new Set(['headline', 'summary']);
const PERSONAL_INFO_FIELDS = new Set<string>(['firstName', 'lastName']);
const CONTACT_FIELDS = new Set<string>(['email', 'phone', 'location']);
const EMPLOYMENT_FIELDS = new Set(['title', 'company', 'responsibilities']);
const EDUCATION_FIELDS = new Set<string>(['degree', 'field', 'institution', 'gpa']);
const CERTIFICATION_FIELDS = new Set<string>(['name', 'issuer', 'credentialUrl']);
const SKILL_LISTS = new Set<string>(['technical', 'soft']);
const CUSTOM_SECTION_FIELDS = new Set(['title', 'content']);

/** Date sub-fields, by the record that owns them. */
const DATE_FIELDS: Record<DateOwner, Set<string>> = {
  employment: new Set(['startDate', 'endDate']),
  education: new Set(['startDate', 'endDate']),
  certification: new Set(['date', 'issueDate', 'expiryDate']),
};

const DATE_OWNER_BY_SECTION: Record<string, DateOwner> = {
  employment: 'employment',
  education: 'education',
  certifications: 'certification',
};

/**
 * Skills are stored as `string[]` but edited as one block of text, one skill per
 * line — the same shape responsibilities already use, so the caret, the mobile
 * sheet and the list parsing all behave identically.
 */
export function skillsToText(skills: string[] | undefined): string {
  return (skills ?? []).join('\n');
}

/** Inverse of {@link skillsToText}. Tolerates the markers a list edit may leave. */
export function textToSkills(value: string): string[] {
  return value
    .split('\n')
    .map((line) => line.replace(/^\s*(?:[-*\u2022\u2023\u25E6]|\d+[.)])\s+/, '').trim())
    .filter((line) => line.length > 0);
}

/**
 * Turn a stamped path plus its new text into an instruction the store can act
 * on. Returns `null` for anything unrecognised, so a stray or hand-edited
 * attribute can never write to an arbitrary part of the resume.
 */
export function parseFieldEdit(path: string, value: string): FieldEdit | null {
  const parts = path.split('.');

  if (parts.length === 2) {
    const [section, field] = parts;

    if (section === 'profile' && PROFILE_FIELDS.has(field)) {
      return { kind: 'profile', field: field as 'headline' | 'summary', value };
    }
    if (section === 'personalInfo' && PERSONAL_INFO_FIELDS.has(field)) {
      return { kind: 'personalInfo', field: field as PersonalInfoField, value };
    }
    if (section === 'contact' && CONTACT_FIELDS.has(field)) {
      return { kind: 'contact', field: field as ContactField, value };
    }
    if (section === 'skills' && SKILL_LISTS.has(field)) {
      return { kind: 'skillList', list: field as SkillListField, value };
    }
  }

  if (parts.length === 3) {
    const [section, id, field] = parts;
    if (!id) return null;

    // Dates first: they share a section with text fields but are never edited
    // as text, so they must not fall through to the text branches below.
    const dateOwner = DATE_OWNER_BY_SECTION[section];
    if (dateOwner && DATE_FIELDS[dateOwner].has(field)) {
      return { kind: 'date', owner: dateOwner, id, field, value };
    }

    if (section === 'employment' && EMPLOYMENT_FIELDS.has(field)) {
      return { kind: 'employment', id, field: field as 'title' | 'company' | 'responsibilities', value };
    }
    if (section === 'education' && EDUCATION_FIELDS.has(field)) {
      return { kind: 'education', id, field: field as EducationField, value };
    }
    if (section === 'certifications' && CERTIFICATION_FIELDS.has(field)) {
      return { kind: 'certification', id, field: field as CertificationField, value };
    }
    if (section === 'skills' && SKILL_LISTS.has(id) && /^\d+$/.test(field)) {
      return { kind: 'skillItem', list: id as SkillListField, index: Number(field), value };
    }
    if (section === 'skillCategories' && field === 'skills') {
      return { kind: 'skillCategory', id, value };
    }
    if (section === 'skillCategories' && /^\d+$/.test(field)) {
      return { kind: 'skillCategoryItem', id, index: Number(field), value };
    }
    if (section === 'customSections' && CUSTOM_SECTION_FIELDS.has(field)) {
      return { kind: 'customSection', id, field: field as 'title' | 'content', value };
    }
  }

  return null;
}

/** Current value behind a stamped path, or `null` when the path does not resolve. */
export function readFieldValue(content: ResumeContent, path: string): string | null {
  const edit = parseFieldEdit(path, '');
  if (!edit) return null;

  if (edit.kind === 'profile') return content.profile?.[edit.field] ?? '';
  if (edit.kind === 'personalInfo') return content.personalInfo?.[edit.field] ?? '';
  if (edit.kind === 'contact') return content.contact?.[edit.field] ?? '';
  if (edit.kind === 'skillList') return skillsToText(content.skills?.[edit.list]);

  if (edit.kind === 'skillItem') {
    return content.skills?.[edit.list]?.[edit.index] ?? '';
  }

  if (edit.kind === 'skillCategoryItem') {
    const category = content.skills?.categories?.find((item) => item.id === edit.id);
    return category?.skills?.[edit.index] ?? '';
  }

  if (edit.kind === 'skillCategory') {
    const category = content.skills?.categories?.find((item) => item.id === edit.id);
    return category ? skillsToText(category.skills) : null;
  }

  if (edit.kind === 'education') {
    const entry = content.education?.find((item) => item.id === edit.id);
    return entry ? ((entry[edit.field] as string | undefined) ?? '') : null;
  }

  if (edit.kind === 'certification') {
    const entry = content.certifications?.find((item) => item.id === edit.id);
    return entry ? ((entry[edit.field] as string | undefined) ?? '') : null;
  }

  if (edit.kind === 'date') {
    const list =
      edit.owner === 'employment'
        ? content.employment
        : edit.owner === 'education'
          ? content.education
          : content.certifications;
    const entry = (list as unknown as Array<Record<string, unknown>> | undefined)?.find(
      (item) => item.id === edit.id,
    );
    return entry ? ((entry[edit.field] as string | undefined) ?? '') : null;
  }

  if (edit.kind === 'employment') {
    const entry = content.employment?.find((item) => item.id === edit.id);
    return entry ? ((entry[edit.field] as string | undefined) ?? '') : null;
  }

  const section = content.customSections?.find((item) => item.id === edit.id);
  return section ? ((section[edit.field] as string | undefined) ?? '') : null;
}

/**
 * Convert edited markup back into the plain text the field stores.
 *
 * The renderer expands a field's newlines into `<br>` or, since list support
 * landed, into `<ul><li>` — so reading `textContent` back would silently weld
 * every bullet into one paragraph, which is the exact bug list support fixed.
 * List items come back with their `- ` marker so the next render rebuilds the
 * same list.
 */
/**
 * One character of a field's text, with the DOM position it came from.
 *
 * Synthetic characters — a list item's `- ` marker, the newline between lines —
 * have no DOM origin and carry `node: null`.
 */
interface FieldChar {
  ch: string;
  node: Node | null;
  offset: number;
}

const isSpace = (ch: string) => ch === ' ' || ch === '\t';

/**
 * Build the field's text together with per-character provenance.
 *
 * This is the single source of truth for BOTH directions of the mapping between
 * a DOM position and an offset in the stored text. They used to be written
 * separately — `caretOffsetInField` counted raw `nodeValue` lengths while
 * `htmlToFieldText` normalized whitespace and added `- ` markers — so the two
 * disagreed, and the disagreement grew with every bullet. Clicking into a list
 * put the caret several characters off, and once the accumulated markers pushed
 * the offset past the raw DOM length the caret jumped to the very end.
 *
 * Deriving both directions from one walk makes that class of bug impossible.
 */
function buildFieldChars(root: Node): FieldChar[] {
  const lines: FieldChar[][] = [];
  let current: FieldChar[] = [];

  /** Apply the per-line rules: collapse spacing runs, trim both edges. */
  const normalizeLine = (line: FieldChar[]): FieldChar[] => {
    const collapsed: FieldChar[] = [];
    for (const entry of line) {
      // A run of spacing becomes a single space, keeping the first one's origin.
      if (isSpace(entry.ch) && collapsed.length > 0 && isSpace(collapsed[collapsed.length - 1].ch)) {
        continue;
      }
      collapsed.push(isSpace(entry.ch) ? { ...entry, ch: ' ' } : entry);
    }
    let from = 0;
    let to = collapsed.length;
    while (from < to && /\s/.test(collapsed[from].ch)) from += 1;
    while (to > from && /\s/.test(collapsed[to - 1].ch)) to -= 1;
    return collapsed.slice(from, to);
  };

  const flush = () => {
    lines.push(normalizeLine(current));
    current = [];
  };

  const hasContent = (line: FieldChar[]) => line.some((entry) => !/\s/.test(entry.ch));

  const walk = (node: Node) => {
    if (node.nodeType === 3 /* text */) {
      const value = node.nodeValue ?? '';
      for (let i = 0; i < value.length; i += 1) {
        current.push({ ch: value[i], node, offset: i });
      }
      return;
    }
    if (node.nodeType !== 1 /* element */) return;

    const element = node as Element;
    const tag = element.tagName.toLowerCase();

    if (tag === 'br') {
      flush();
      return;
    }

    const isListItem = tag === 'li';
    const isBlock = /^(p|div|li|ul|ol|h[1-6])$/.test(tag);

    if (isBlock && hasContent(current)) flush();
    // Replacing `current` also discards any inter-tag indentation gathered
    // since the last flush: that whitespace is layout, not content.
    if (isListItem) {
      current = [
        { ch: '-', node: null, offset: 0 },
        { ch: ' ', node: null, offset: 0 },
      ];
    }

    for (const child of Array.from(element.childNodes)) walk(child);

    if (isBlock && hasContent(current)) flush();
  };

  walk(root);
  if (hasContent(current)) flush();

  // Collapse runs of blank lines but keep deliberate single separators, then
  // trim the whole thing — mirroring the string form exactly.
  const out: FieldChar[] = [];
  let blankRun = 0;
  lines.forEach((line, index) => {
    const blank = line.length === 0;
    blankRun = blank ? blankRun + 1 : 0;
    // A third or later consecutive blank line collapses away.
    if (blank && blankRun > 1) return;
    if (index > 0 && out.length > 0) out.push({ ch: '\n', node: null, offset: 0 });
    else if (index > 0 && out.length === 0) return; // leading blanks trim away
    out.push(...line);
  });

  while (out.length > 0 && /\s/.test(out[0].ch)) out.shift();
  while (out.length > 0 && /\s/.test(out[out.length - 1].ch)) out.pop();

  return out;
}

/**
 * Convert edited markup back into the plain text the field stores.
 *
 * The renderer expands a field's newlines into `<br>` or, since list support
 * landed, into `<ul><li>` — so reading `textContent` back would silently weld
 * every bullet into one paragraph, which is the exact bug list support fixed.
 * List items come back with their `- ` marker so the next render rebuilds the
 * same list.
 */
export function htmlToFieldText(root: Node): string {
  return buildFieldChars(root)
    .map((entry) => entry.ch)
    .join('');
}

/** Offset in the field's text for a DOM position. */
export function domPositionToFieldOffset(root: Node, node: Node, nodeOffset: number): number {
  const chars = buildFieldChars(root);

  for (let i = 0; i < chars.length; i += 1) {
    if (chars[i].node === node && chars[i].offset >= nodeOffset) return i;
  }

  // The caret sits after this node's last contributing character (end of a
  // line, or on trailing whitespace the text dropped).
  for (let i = chars.length - 1; i >= 0; i -= 1) {
    if (chars[i].node === node) return i + 1;
  }

  return chars.length;
}

/**
 * DOM position for an offset in the field's text — the inverse of
 * {@link domPositionToFieldOffset}, and derived from the same walk so the two
 * can never drift apart.
 */
export function fieldOffsetToDomPosition(
  root: Node,
  offset: number
): { node: Node; offset: number } | null {
  const chars = buildFieldChars(root);
  const clamped = Math.max(0, Math.min(offset, chars.length));

  // Landing squarely on a real character is the common case.
  const target = chars[clamped];
  if (target?.node) return { node: target.node, offset: target.offset };

  /*
   * Otherwise the offset points at a synthetic character — the newline ending a
   * line, or a list item's "- " marker — which has no DOM position of its own.
   * Anchor just AFTER the previous real character rather than scanning forward
   * to the next one. Scanning forward would move a caret clicked at the end of
   * a bullet onto the start of the following bullet, which is exactly the kind
   * of jump this mapping exists to prevent.
   */
  for (let i = clamped - 1; i >= 0; i -= 1) {
    if (chars[i].node) return { node: chars[i].node!, offset: chars[i].offset + 1 };
  }
  // Nothing real before it: the first real character, or an empty field.
  for (let i = clamped; i < chars.length; i += 1) {
    if (chars[i].node) return { node: chars[i].node!, offset: chars[i].offset };
  }
  return null;
}

/**
 * Whether a field should be edited as multi-line text.
 *
 * Single-line fields commit on Enter; multi-line ones treat Enter as a new
 * bullet or paragraph and need an explicit commit.
 */
export function isMultilineField(path: string): boolean {
  return /\.(responsibilities|content)$/.test(path) || path === 'profile.summary';
}
